import React from "react";
import { useTranslation } from "react-i18next";
import { Check, Tag, X } from "lucide-react";
import { formatBRL } from "../lib/api.js";

export function CouponSection({
  open,
  draft,
  applied,
  discountCents,
  error,
  onToggle,
  onChangeDraft,
  onApply,
  onRemove,
}: {
  open: boolean;
  draft: string;
  applied: string;
  discountCents: number;
  error: string | null;
  onToggle: () => void;
  onChangeDraft: (v: string) => void;
  onApply: () => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation();

  if (applied) {
    return (
      <div className="rounded-lg border border-success/30 bg-success/[0.06] px-3 py-2.5 flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm">
          <Check className="w-4 h-4 text-success" />
          <span className="font-medium">{applied}</span>
          <span className="text-ink-2">— {t("checkout.couponApplied")} (−{formatBRL(discountCents)})</span>
        </div>
        <button type="button" onClick={onRemove} aria-label={t("checkout.removeCoupon")} className="text-ink-2 hover:text-text-primary">
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <div>
      {!open ? (
        <button
          type="button"
          onClick={onToggle}
          className="inline-flex items-center gap-1.5 text-sm hover:opacity-80"
          style={{ color: "var(--purple-deep)" }}
        >
          <Tag className="w-3.5 h-3.5" />
          {t("checkout.addCoupon")}
        </button>
      ) : (
        <div>
          <div className="flex gap-2 items-stretch">
            <input
              className="input-clean flex-1"
              placeholder={t("checkout.couponPlaceholder")}
              value={draft}
              autoFocus
              onChange={(e) => onChangeDraft(e.target.value.toUpperCase())}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  onApply();
                } else if (e.key === "Escape") {
                  e.preventDefault();
                  onToggle();
                }
              }}
            />
            <button type="button" onClick={onApply} className="btn-ghost px-4 whitespace-nowrap" disabled={!draft.trim()}>
              {t("checkout.applyCoupon")}
            </button>
            <button
              type="button"
              onClick={onToggle}
              aria-label={t("checkout.closeCoupon")}
              title={t("checkout.closeCoupon")}
              className="inline-flex items-center justify-center w-10 rounded-md text-ink-2 hover:text-text-primary hover:bg-black/5 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <button type="button" onClick={onToggle} className="text-xs text-ink-2 hover:text-text-primary mt-1.5 underline">
            {t("checkout.closeCoupon")}
          </button>
        </div>
      )}
      {error === "not_found" && <p className="text-xs text-danger mt-1.5">{t("checkout.couponInvalid")}</p>}
    </div>
  );
}
